import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle, Route } from "lucide-react";
import { roleLabel } from "@/lib/roles";

export const FIX_LABELS = {
  project: "Project", budget_line: "Head / Sub-type", amount: "Amount", expense_month: "Expense Month", purpose: "Purpose",
  particulars: "Line items", invoice_number: "Vendor bill no.", invoice_date: "Bill date", vendor: "Vendor", attachments: "Attachments",
  notes: "Notes", expected_settlement_date: "Expected settlement",
};
const roleName = (r) => (r === "Admin" ? "Super Admin" : r ? roleLabel(r) : "Reviewer");

export function useFileNames(ids, setFiles) {
  useEffect(() => {
    if (!ids?.length) return;
    api.get(`/files/meta?ids=${ids.join(",")}`).then(r => {
      const m = Object.fromEntries(r.data.map(x => [x.id, x]));
      setFiles(fs => fs.map(f => (m[f.id] ? { ...f, ...m[f.id] } : f)));
    }).catch(() => {});
  }, []);
}

export const hlFor = (fields) => (k) => ((fields || []).includes(k) ? "ring-2 ring-rose-400 rounded-md p-1 bg-rose-50/40" : "");

export function fileChange(oldIds, newIds) {
  const o = oldIds || [], n = newIds || [];
  if (o.some(x => !n.includes(x)) && n.some(x => !o.includes(x))) return "Attachment replaced";
  if (o.some(x => !n.includes(x))) return "Attachment removed";
  if (n.some(x => !o.includes(x))) return "Attachment added";
  return null;
}

export function diffList(pairs, extra = []) {
  const out = pairs.filter(([, o, n]) => String(o ?? "") !== String(n ?? "")).map(([l, o, n]) => `${l} ${o || "—"} → ${n || "—"}`);
  return [...out, ...extra.filter(Boolean)];
}

export function SentBackBanner({ doc, invRef }) {
  if (!doc) return null;
  const fields = doc.sent_back_fields || [];
  return (
    <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm space-y-1" data-testid="er-sentback-banner">
      <div className="flex items-center gap-1.5 text-rose-800 font-semibold"><AlertTriangle className="w-4 h-4" /> Sent back for correction</div>
      <div className="text-rose-900" data-testid="er-sentback-reason">{doc.sent_back_reason || "(no reason given)"}</div>
      <div className="text-xs text-rose-700" data-testid="er-sentback-by">— {doc.sent_back_by_name || "Reviewer"}, {roleName(doc.sent_back_by_role)} · {(doc.sent_back_at || "").slice(0, 10)}</div>
      {fields.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1" data-testid="er-fix-fields">
          <span className="text-xs text-rose-800">Please correct:</span>
          {fields.map(f => <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-600 text-white" data-testid={`er-fix-${f}`}>{FIX_LABELS[f] || f}</span>)}
        </div>
      )}
      {invRef && <div className="text-xs text-slate-600">RKF ref <span className="font-mono font-semibold" data-testid="er-inv-ref">{invRef}</span> stays the same.</div>}
    </div>
  );
}

export function ResubmitFooter({ kind, doc, changes, material, busy, onSave, onWithdrawn, blockReason }) {
  const [response, setResponse] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");
  const dirty = changes.length > 0;
  const withdraw = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    try { await api.post(`/chain/${kind}/${doc.id}/withdraw`, { reason }); toast.success("Withdrawn"); onWithdrawn(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const route = material
    ? "Goes back to the first approval stage because the amount/budget line changed"
    : `Goes back to [${roleName(doc.sent_back_by_role)} – ${doc.sent_back_by_name || "reviewer"}]`;
  return (
    <div className="space-y-3 border-t pt-3" data-testid="er-footer">
      <div className={`rounded-md border p-2 text-sm ${dirty ? "border-blue-200 bg-blue-50 text-blue-900" : "border-slate-200 bg-slate-50 text-slate-500"}`} data-testid="er-changes">
        {dirty ? <><b>You changed:</b> {changes.join("; ")}.</> : "No changes yet – change at least one field or attachment to resubmit."}
      </div>
      {dirty && <div className="text-xs flex items-center gap-1 text-slate-700" data-testid="er-route"><Route className="w-3.5 h-3.5" /> {route}</div>}
      <div>
        <Label>Your reply (required)</Label>
        <Textarea rows={2} value={response} onChange={e => setResponse(e.target.value)} placeholder="What did you correct?" data-testid="er-reply" />
      </div>
      {blockReason && <p className="text-xs text-rose-600" data-testid="er-block">{blockReason}</p>}
      {withdrawing && (
        <div className="flex gap-2">
          <Input placeholder="Reason for withdrawing" value={reason} onChange={e => setReason(e.target.value)} data-testid="er-withdraw-reason" />
          <Button type="button" variant="destructive" onClick={withdraw} data-testid="er-withdraw-confirm">Confirm withdraw</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button type="button" variant="ghost" className="text-rose-700" onClick={() => setWithdrawing(true)} data-testid="er-withdraw-btn">Withdraw request</Button>
        <Button type="button" variant="outline" disabled={busy || !!blockReason} onClick={() => onSave("draft", response)} data-testid="er-save-draft-btn">{busy ? "Saving…" : "Save draft"}</Button>
        <Button type="button" disabled={busy || !dirty || !response.trim() || !!blockReason} onClick={() => onSave("resubmit", response)} data-testid="er-resubmit-btn">{busy ? "Saving…" : "Resubmit"}</Button>
      </div>
    </div>
  );
}

export function RejectedNote({ doc, copyLink, testId }) {
  const h = [...(doc.history || [])].reverse().find(x => (x.action || "").toLowerCase() === "rejected") || {};
  return (
    <div className="rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800 flex items-center gap-2 flex-wrap" data-testid={testId}>
      <span><b>Rejected by {h.by_name || doc.rejected_by_name || "reviewer"}:</b> {doc.rejection_reason || h.comment || "—"}</span>
      {copyLink && <a href={copyLink}><Button size="sm" variant="outline" className="h-7" data-testid={`${testId}-copy`}>Copy as new claim</Button></a>}
    </div>
  );
}
